import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { autoMobileVehicleService } from '../../../../services/autoMobileVehicleService';
import { ArrowLeft, SquarePen, Trash2, Car } from 'lucide-react';
import '../AutoMobilePage.css';

const VehicleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await autoMobileVehicleService.getById(parseInt(id));
      const d = (res.data as any)?.data ?? res.data;
      setItem(d);
    } catch {
      setError(t('automobile.vehicle.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    if (!window.confirm(t('automobile.vehicle.deleteConfirm'))) return;
    setDeleting(true);
    try {
      await autoMobileVehicleService.delete(item.id);
      navigate('/automobile/vehicles');
    } catch {
      setError(t('automobile.vehicle.failedToDelete'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-page auto-page" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="ims-page auto-page">
          <div className="ims-alert ims-alert-error">{error ?? t('automobile.vehicle.notFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ims-detail-page auto-detail-page">
        <div className="ims-hero auto-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/automobile/vehicles')}>
              <ArrowLeft size={14} />
              {t('automobile.vehicle.title')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{item.registrationNumber}</h1>
              <p className="ims-hero-supplier">{item.make} {item.model} · {item.year} · {item.customerName}</p>
              <div className="ims-hero-badges">
                <span className={`ims-hero-badge ${item.isActive ? 'auto-badge-active' : 'auto-badge-inactive'}`}>
                  {item.isActive ? t('common.active') : t('common.inactive')}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="ims-btn ims-btn-secondary" onClick={() => navigate(`/automobile/vehicles/${item.id}/edit`)}>
                  <SquarePen size={14} style={{ marginInlineEnd: 4 }} />
                  {t('common.edit')}
                </button>
                <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                  <Trash2 size={14} style={{ marginInlineEnd: 4 }} />
                  {deleting ? t('common.deleting') : t('common.delete')}
                </button>
              </div>
              <span className="ims-hero-date">
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBlock: '1rem' }}>
            {error}
            <button className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <div className="ims-detail-body">
          <div className="ims-detail-main">
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">
                  <Car size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {t('automobile.vehicle.details')}
                </span>
              </div>
              <div className="ims-card-body">
                <div className="auto-fields-grid">
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.registrationNumber')}</div>
                    <div className="auto-field-value strong">{item.registrationNumber}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.vin')}</div>
                    <div className="auto-field-value">{item.vin || '—'}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.make')}</div>
                    <div className="auto-field-value">{item.make}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.model')}</div>
                    <div className="auto-field-value">{item.model}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.year')}</div>
                    <div className="auto-field-value">{item.year}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.color')}</div>
                    <div className="auto-field-value">{item.color || '—'}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.mileage')}</div>
                    <div className="auto-field-value">{item.mileage?.toLocaleString?.() ?? item.mileage}</div>
                  </div>
                  <div>
                    <div className="auto-field-label">{t('automobile.vehicle.customer')}</div>
                    <div className="auto-field-value">{item.customerName}</div>
                  </div>
                  <div className="auto-field-full">
                    <div className="auto-field-label">{t('common.notes')}</div>
                    <div className="auto-field-value">{item.notes || '—'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VehicleDetailPage;
